import React, { Suspense, useCallback, useMemo } from "react";
import { FixedSizeList as List } from "react-window";
import { ListDetail } from "../web/ListDetail";
import { useFiltersv2 } from "../core/useFilters";
import { FilterList } from "../core/FilterList";
import { ListView } from "../core/ListView";
import Skeleton from "@mui/material/Skeleton";
import Box from "@mui/material/Box";
import { applyAdvancedFilters } from "./applyAdvancedFilters";
import { useAdvancedFilters } from "./useAdvancedFilters";

const Empty = () => <Box />;

/**
 * We use a structural "query result like" type instead of React Query's discriminated union.
 * Some of our hooks provide `placeholderData: []` (so `data` is always an array), while others
 * may have `data` undefined in certain states. For our list UIs we only need a small subset of
 * the query result shape.
 *
 * @template T
 * @typedef {{
 *  data: T[] | undefined,
 *  refetch: () => Promise<unknown>,
 *  isLoading: boolean,
 *  isRefetching: boolean,
 *  isError: boolean,
 * }} QueryResultLike
 */

/**
 * @template T
 * @param {({ item, filteredIndex, filteredCount, style }) => React.JSX.Element} ListEntry
 * @param {(item: T) => string} keyExtractor
 * @param {string} title
 * @param {() => React.JSX.Element} EmptyList
 * @param {(any) => React.JSX.Element} [Action]
 * @param {() => React.JSX.Element|undefined} [ListHeader]
 * @param {import("react-router-dom").To} [back]
 * @param {boolean} [bigList]
 * @param {(() => React.JSX.Element)|null} [Footer]
 * @returns {(queryResult: QueryResultLike<T>, filterConfiguration: import("@seabrookstudios/pitch2table-core").Filters<T>) => () => React.JSX.Element}
 */
export const useMakeListBuilder = (
  ListEntry,
  keyExtractor,
  title,
  EmptyList,
  Action,
  ListHeader,
  back,
  bigList = false,
  Footer = null
) => {
  /**
   * @template T
   * @param {QueryResultLike<T>} queryResult
   * @param {import("@seabrookstudios/pitch2table-core").Filters<T>} filters
   * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} [advancedFilters]
   * @returns {() => React.JSX.Element}
   */
  const useMakeList = (queryResult, filters, advancedFilters = []) => {
    const Filters = useCallback(
      () => (
        <FilterList
          filters={filters.visibleFilters}
          filter={filters.filter}
          onFilterClicked={filters.onFilterClicked}
        />
      ),
      [filters]
    );

    const ListEntryWrapper = ({ style, index, data }) => {
      const item = data[index];
      return <ListEntry item={item} filteredIndex={index} filteredCount={data.length} style={style} />;
    };

    const BuiltList = () => {
      const visibleAdvancedFilters = useMemo(
        () => advancedFilters.filter((f) => (f.hideOn ? !f.hideOn.includes("web") : true)),
        [advancedFilters]
      );

      const { selectionsByLabel: advancedSelectionsByLabel, setSelectionsByLabel: setAdvancedSelectionsByLabel } =
        useAdvancedFilters(visibleAdvancedFilters);

      const advancedFiltered = useMemo(() => {
        if (visibleAdvancedFilters.length === 0) {
          return filters.filtered;
        }
        return applyAdvancedFilters(filters.filtered, visibleAdvancedFilters, advancedSelectionsByLabel);
      }, [filters.filtered, visibleAdvancedFilters, advancedSelectionsByLabel]);

      return (
        <ListView
          back={back}
          title={title}
          Filters={Filters}
          Action={Action}
          EmptyList={EmptyList}
          shown={advancedFiltered.length}
          refetch={queryResult.refetch}
          isLoading={queryResult.isLoading}
          isRefetching={queryResult.isRefetching}
          isError={queryResult.isError}
          advancedFilters={visibleAdvancedFilters}
          advancedFilterSelectionsByLabel={advancedSelectionsByLabel}
          onAdvancedFilterSelectionsByLabelChange={setAdvancedSelectionsByLabel}
          Footer={Footer}
        >
          {ListHeader && <ListHeader />}
          {bigList ? (
            <List height={500} itemCount={advancedFiltered.length} itemSize={60} itemData={advancedFiltered}>
              {ListEntryWrapper}
            </List>
          ) : (
            <Suspense fallback={<Skeleton />}>
              {advancedFiltered.map((item, filteredIndex) => (
                <ListEntry
                  // @ts-expect-error - keyExtractor is a function that returns a string
                  key={keyExtractor(item)}
                  item={item}
                  filteredIndex={filteredIndex}
                  filteredCount={advancedFiltered.length}
                  style={{}}
                />
              ))}
            </Suspense>
          )}
        </ListView>
      );
    };

    return BuiltList;
  };

  return useMakeList;
};

/**
 * @param {object} item
 * @returns {string}
 */
export const idExtractor = (item) => item.id.toString();

/**
 * @template T
 * @param {() => QueryResultLike<T>} query
 * @param {Filter<T>[]} filters
 * @param {string} filterKey
 * @param {(queryResult: QueryResultLike<T>, filterConfiguration: Filters<T>, AdvancedFilters: import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]) => () => React.JSX.Element} useMakeList
 * @param {() => React.JSX.Element} [DetailView]
 * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} [advancedFilters]
 * @returns {() => React.JSX.Element}
 */
export const useMakeListAndDetail = (query, filters, filterKey, useMakeList, DetailView, advancedFilters = []) => {
  const queryResult = query();
  const filterConfiguration = useFiltersv2(/** @type {any} */ (queryResult), filters, filterKey);
  const ListView = useMakeList(queryResult, filterConfiguration, advancedFilters);

  const ListAndDetail = () => <ListDetail ListView={ListView} DetailView={DetailView || Empty} />;
  return ListAndDetail;
};

/**
 * @template T
 * @param {() => QueryResultLike<T>} query
 * @param {Filter<T>[]} filters
 * @param {string} filterKey
 * @param {(queryResult: QueryResultLike<T>, filterConfiguration: Filters<T>, advancedFilters?: import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]) => () => React.JSX.Element} useMakeList
 * @param {import("@seabrookstudios/pitch2table-core").AdvancedFilter<T>[]} [advancedFilters]
 * @returns {() => React.JSX.Element}
 */
export const useMakeListView = (query, filters = [], filterKey, useMakeList, advancedFilters = []) => {
  const queryResult = query();
  const filterConfiguration = useFiltersv2(/** @type {any} */ (queryResult), filters, filterKey);
  const ListView = useMakeList(queryResult, filterConfiguration, advancedFilters);

  return ListView;
};
